import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/store'
import {
	selectSelectedOrderType,
	selectIsDeferred,
	selectExpectedTimeUtc,
	selectCartItemCount,
	selectCartTotal,
} from '@/store/ticketSlice'
import { useLocationRef } from '@/contexts/RefContext'
import { PATHS } from '@/router/paths'
import { formatPrice } from '@/lib/formatPrice'
import { convertMinutesToHoursAndMinutes } from '@/lib/timeUtils'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { formatScheduledTime } from '@/lib/formatScheduledTime'
import { useMenuItems } from '@/pages/menu/hooks/useMenuItems'
import { OrderDetailsPanel } from './OrderDetailsPanel'
import {
	BottomBarOverlay,
	BottomBarCard,
	BottomBarInner,
	BottomBarDetailsRow,
	BottomBarDetailsPairs,
	BottomBarDetailPair,
	BottomBarTimingPair,
	BottomBarOrderTypeLabel,
	BottomBarTimingText,
	BottomBarTimingBold,
	BottomBarEditButton,
	BottomBarCheckoutRow,
	BottomBarCheckoutButton,
	BottomBarItemBadge,
	DrawerScrim,
	DrawerCard,
	DrawerHeader,
	DrawerHeading,
	DrawerHeadingCount,
	DrawerCloseButton,
	DrawerBody,
} from './CartBottomBar.styled'

interface CartBottomBarProps {
	scheduleId: string
}

export function CartBottomBar({ scheduleId }: CartBottomBarProps) {
	const intl = useIntl()
	const { t } = useFormatMessage()
	const navigate = useNavigate()
	const locationRef = useLocationRef()

	const orderType = useAppSelector(selectSelectedOrderType)
	const isDeferred = useAppSelector(selectIsDeferred)
	const expectedTimeUtc = useAppSelector(selectExpectedTimeUtc)
	const itemCount = useAppSelector(selectCartItemCount)
	const cartTotal = useAppSelector(selectCartTotal)

	const { data: itemsData } = useMenuItems(locationRef, scheduleId)
	const menuItems = useMemo(() => {
		const map: Record<string, NonNullable<typeof itemsData>['items'][number]> = {}
		if (itemsData) {
			for (const item of itemsData.items) {
				map[item.ref] = item
			}
		}
		return map
	}, [itemsData])

	const [drawerOpen, setDrawerOpen] = useState(false)

	// Refs for focus management — WCAG 2.1 AA requires focus to move into a modal
	// dialog on open and return to the trigger element on close.
	const closeButtonRef = useRef<HTMLButtonElement>(null)
	const triggerButtonRef = useRef<HTMLButtonElement>(null)
	const drawerCardRef = useRef<HTMLDivElement>(null)

	// Move focus to the close button when the drawer opens.
	// setTimeout(0) defers until after the conditional render mounts the drawer DOM.
	useEffect(() => {
		if (!drawerOpen) return
		const id = setTimeout(() => {
			closeButtonRef.current?.focus()
		}, 0)
		return () => clearTimeout(id)
	}, [drawerOpen])

	const isCartEmpty = itemCount === 0

	const orderTypeName = orderType?.name ?? t('component:cartActionsFooter:orderType:fallback')

	const timingLabel = (() => {
		if (isDeferred && expectedTimeUtc) {
			return {
				bold: '',
				regular: formatScheduledTime(
					expectedTimeUtc,
					intl.locale,
					t('orderSettings:todayLabel'),
				),
			}
		}
		const leadMins = orderType?.pickupLeadTimeInMinutes
		const duration =
			leadMins != null
				? `${intl.formatMessage({ id: 'orderSettings:asapSubtitleAbout' })} ${convertMinutesToHoursAndMinutes(leadMins)}`
				: intl.formatMessage({ id: 'orderSettings:asapSubtitleFallback' })
		return { bold: t('orderSettings:asapLabel'), regular: ` ${duration}` }
	})()

	const formattedTotal = formatPrice(cartTotal, intl.locale, 'USD')

	function handleEditOrderSettings() {
		void navigate(`${PATHS.orderSettings(locationRef)}?edit=1`)
	}

	function handleReviewOrder() {
		if (isCartEmpty) return
		setDrawerOpen(true)
	}

	function handleCloseDrawer() {
		setDrawerOpen(false)
		// Return focus to the trigger so keyboard users don't lose their position.
		triggerButtonRef.current?.focus()
	}

	function handleScrimKeyDown(e: React.KeyboardEvent) {
		if (e.key === 'Escape') {
			handleCloseDrawer()
			return
		}

		// Focus trap — keep Tab/Shift+Tab cycling within DrawerCard (WCAG 2.1 AA).
		// aria-modal="true" prevents AT from reaching outside content but does not
		// physically constrain keyboard focus — we must do that ourselves.
		if (e.key !== 'Tab') return

		const card = drawerCardRef.current
		if (!card) return

		const focusable = Array.from(
			card.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			),
		).filter((el) => !el.hasAttribute('disabled'))

		if (focusable.length === 0) return

		const first = focusable[0]
		const last = focusable[focusable.length - 1]

		if (e.shiftKey) {
			if (document.activeElement === first) {
				e.preventDefault()
				last.focus()
			}
		} else {
			if (document.activeElement === last) {
				e.preventDefault()
				first.focus()
			}
		}
	}

	return (
		<>
			<BottomBarOverlay>
				<BottomBarCard>
					<BottomBarInner>
						{/* Order details row: bag icon + type | clock icon + timing | edit icon */}
						<BottomBarDetailsRow>
							<BottomBarDetailsPairs>
								<BottomBarDetailPair>
									<ShoppingBagOutlinedIcon
										sx={{ fontSize: 20, flexShrink: 0 }}
										aria-hidden="true"
									/>
									<BottomBarOrderTypeLabel>
										{orderTypeName}
									</BottomBarOrderTypeLabel>
								</BottomBarDetailPair>

								<BottomBarTimingPair>
									<AccessTimeIcon
										sx={{ fontSize: 20, flexShrink: 0 }}
										aria-hidden="true"
									/>
									<BottomBarTimingText>
										{timingLabel.bold ? (
											<BottomBarTimingBold>
												{timingLabel.bold}
											</BottomBarTimingBold>
										) : null}
										{timingLabel.regular}
									</BottomBarTimingText>
								</BottomBarTimingPair>
							</BottomBarDetailsPairs>

							<BottomBarEditButton
								onClick={handleEditOrderSettings}
								aria-label={t(
									'component:cartActionsFooter:editOrderSettings:label',
								)}
							>
								<EditOutlinedIcon sx={{ fontSize: 20 }} aria-hidden="true" />
							</BottomBarEditButton>
						</BottomBarDetailsRow>

						{/* Review Order button — opens the order details drawer.
						    Badge shows item count inside the button (Figma 562:40895). */}
						<BottomBarCheckoutRow>
							<BottomBarCheckoutButton
								ref={triggerButtonRef}
								isDisabled={isCartEmpty}
								disabled={isCartEmpty}
								onClick={handleReviewOrder}
								aria-label={t('component:cartBottomBar:reviewOrder:ariaLabel', {
									total: formattedTotal,
								})}
								aria-haspopup="dialog"
							>
								{t('component:cartBottomBar:reviewOrder:label', {
									total: formattedTotal,
								})}
								{!isCartEmpty && (
									<BottomBarItemBadge aria-hidden="true">
										{itemCount}
									</BottomBarItemBadge>
								)}
							</BottomBarCheckoutButton>
						</BottomBarCheckoutRow>
					</BottomBarInner>
				</BottomBarCard>
			</BottomBarOverlay>

			{/* Order details drawer — Figma 562:42740 */}
			{drawerOpen && (
				<DrawerScrim
					role="dialog"
					aria-modal="true"
					aria-label={t('component:orderDetailsPanel:heading:label')}
					onClick={handleCloseDrawer}
					onKeyDown={handleScrimKeyDown}
				>
					<DrawerCard ref={drawerCardRef} onClick={(e) => e.stopPropagation()}>
						<DrawerHeader>
							<DrawerHeading>
								{t('component:orderDetailsPanel:heading:label')}{' '}
								<DrawerHeadingCount>({itemCount})</DrawerHeadingCount>
							</DrawerHeading>
							<DrawerCloseButton
								ref={closeButtonRef}
								onClick={handleCloseDrawer}
								aria-label={t('component:cartBottomBar:drawer:close:label')}
							>
								<CloseIcon fontSize="small" />
							</DrawerCloseButton>
						</DrawerHeader>
						<DrawerBody>
							{/* Gate: do not render until menu items query resolves — Ember parity */}
							{itemsData !== undefined && (
								<OrderDetailsPanel menuItems={menuItems} hideHeader />
							)}
						</DrawerBody>
					</DrawerCard>
				</DrawerScrim>
			)}
		</>
	)
}
